import { addDays, format, startOfDay } from 'date-fns'

import { supabase } from '@/lib/supabase'
import { DEFAULT_TABLE_PAGE_SIZE } from '@/lib/constants'
import { optimizeCourseImage } from '@/lib/image-optimization'
import { env } from '@/lib/env'
import type {
  Booking,
  BookingStatus,
  Course,
  CourseLevel,
  CourseSession,
  Profile,
  PublicSession,
  SubscriptionPackage,
  TrainerCourse,
  UserRole,
  UserSubscription,
  ManagedUserBooking,
  ManagedUserPackage,
  UserPurchasedPackage,
  PaymentEntry,
  ManagedUserPaymentEntry,
  ManagedUserCreditAdjustment,
  VoucherCode,
  VoucherCodeMutationInput,
  VoucherCodeUpdateInput,
  VoucherRedemptionResult,
  VoucherRedemptionReportItem,
  Discount,
  DiscountMutationInput,
  DiscountUpdateInput,
  DiscountMemberAssignment,
  MemberDiscountAssignment,
  HonorariumTier,
  HonorariumNote,
  HonorariumNoteItem,
  HonorariumNoteStatus,
  PurchasedPackageReportItem,
  TokenUsageReportItem,
  AttendanceReportItem,
  FinancialReportItem,
  TrainerActivityReportItem,
  UserGrowthReport,
  PaginatedResponse,
  PublicCourseCatalogCourse,
  Resource,
  ResourceCategory,
  NotificationTemplate,
  SessionRegistrationSettings,
  SessionCancellationSettings,
} from '@/types/domain'

type SortDirection = 'asc' | 'desc'
type UserListSortKey = 'name' | 'email' | 'role' | 'status' | 'created_at'
type BookingListSortKey = 'session' | 'name' | 'email' | 'status'
type UserStatusFilter = 'all' | 'active' | 'inactive'
type ProfilesPaginatedOptions = {
  hideContactData?: boolean
}

type PaginatedResult<T> = {
  items: T[]
  total: number
}

export type AdminProfilesQueryParams = {
  searchTerm: string
  roleFilter: 'all' | UserRole
  statusFilter: UserStatusFilter
  sortKey: UserListSortKey
  sortDirection: SortDirection
  page: number
  pageSize: number
}

export type AdminBookingsQueryParams = {
  statusFilter: BookingStatus | 'all'
  courseIdFilter: string | 'all'
  searchTerm: string
  sortKey: BookingListSortKey
  sortDirection: SortDirection
  page: number
  pageSize: number
}

function one<T>(rows: T[] | null): T {
  if (!rows || rows.length === 0) {
    throw new Error('NOT_FOUND')
  }
  return rows[0]
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return new Error(String((error as { message?: unknown }).message ?? 'UNKNOWN_ERROR'))
  }

  return new Error('UNKNOWN_ERROR')
}

type RpcOptions = {
  count?: 'exact'
}

type RpcResult<T> = {
  data: T | null
  error: unknown
  count?: number | null
}

type RpcQuery<T> = PromiseLike<RpcResult<T>> & {
  range: (from: number, to: number) => PromiseLike<RpcResult<T>>
}

const callRpc = supabase.rpc.bind(supabase) as unknown as <T = unknown>(
  fn: string,
  args?: Record<string, unknown>,
  options?: RpcOptions,
) => RpcQuery<T>

function toPaginationRange(page: number, pageSize: number): { from: number; to: number } {
  const safePage = Math.max(1, Math.trunc(page) || 1)
  const safePageSize = Math.min(
    100,
    Math.max(1, Math.trunc(pageSize) || DEFAULT_TABLE_PAGE_SIZE),
  )
  const from = (safePage - 1) * safePageSize
  const to = from + safePageSize - 1

  return { from, to }
}

function toSearchPattern(value: string): string | null {
  const normalized = value.trim().replace(/\s+/g, ' ')
  if (!normalized) {
    return null
  }

  const sanitized = normalized.replace(/[%_,()]/g, ' ')
  return `%${sanitized}%`
}

function mapVoucherCode(row: Record<string, unknown>): VoucherCode {
  return {
    id: String(row.id),
    code: String(row.code),
    credits: Number(row.credits ?? 0),
    max_redemptions: Number(row.max_redemptions ?? 0),
    redeemed_count: Number(row.redeemed_count ?? 0),
    remaining_redemptions: Number(row.remaining_redemptions ?? 0),
    is_active: Boolean(row.is_active),
    created_at: String(row.created_at),
    deactivated_at: (row.deactivated_at as string | null) ?? null,
  }
}

function mapVoucherRedemptionResult(row: Record<string, unknown>): VoucherRedemptionResult {
  return {
    code: String(row.code),
    credits_granted: Number(row.credits_granted ?? 0),
    total_credits: Number(row.total_credits ?? 0),
    redeemed_at: String(row.redeemed_at),
  }
}

function mapDiscount(row: Record<string, unknown>): Discount {
  return {
    id: String(row.id),
    title: String(row.title),
    discount_type: String(row.discount_type) as Discount['discount_type'],
    percent_off: row.percent_off == null ? null : Number(row.percent_off),
    amount_cents: row.amount_cents == null ? null : Number(row.amount_cents),
    currency: String(row.currency ?? 'eur'),
    is_active: Boolean(row.is_active),
    stripe_coupon_id: (row.stripe_coupon_id as string | null) ?? null,
    stripe_coupon_fingerprint: (row.stripe_coupon_fingerprint as string | null) ?? null,
    assigned_count: Number(row.assigned_count ?? 0),
    active_assigned_count: Number(row.active_assigned_count ?? 0),
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
    deactivated_at: (row.deactivated_at as string | null) ?? null,
  }
}

function mapUserSubscription(row: Record<string, unknown>): UserSubscription {
  const remainingCredits = Number(row.remaining_credits ?? 0)
  const maxSessionsPerWeek =
    row.max_sessions_per_week == null ? null : Number(row.max_sessions_per_week)
  const billingMode =
    row.billing_mode === 'subscription' || (row.billing_mode == null && Boolean(row.is_active))
      ? 'subscription'
      : 'payment'

  return {
    user_id: String(row.user_id),
    package_type: String(row.package_type) as UserSubscription['package_type'],
    package_name: String(row.package_name),
    billing_mode: billingMode,
    remaining_credits: remainingCredits,
    max_sessions_per_week: maxSessionsPerWeek,
    expires_at: (row.expires_at as string | null) ?? null,
    is_active: Boolean(row.is_active),
    stripe_status: (row.stripe_status as string | null) ?? null,
    current_period_end: (row.current_period_end as string | null) ?? null,
    stripe_cancel_at_period_end: Boolean(row.stripe_cancel_at_period_end),
    stripe_cancel_at: (row.stripe_cancel_at as string | null) ?? null,
    stripe_canceled_at: (row.stripe_canceled_at as string | null) ?? null,
  }
}

function mapMemberDiscountAssignment(row: Record<string, unknown>): MemberDiscountAssignment {
  return {
    assignment_id: String(row.assignment_id),
    user_id: String(row.user_id),
    discount_id: String(row.discount_id),
    title: String(row.title),
    discount_type: String(row.discount_type) as MemberDiscountAssignment['discount_type'],
    percent_off: row.percent_off == null ? null : Number(row.percent_off),
    amount_cents: row.amount_cents == null ? null : Number(row.amount_cents),
    currency: String(row.currency ?? 'eur'),
    is_active: Boolean(row.is_active),
    assigned_at: String(row.assigned_at),
    removed_at: (row.removed_at as string | null) ?? null,
  }
}

function mapDiscountMemberAssignment(row: Record<string, unknown>): DiscountMemberAssignment {
  return {
    assignment_id: String(row.assignment_id),
    user_id: String(row.user_id),
    discount_id: String(row.discount_id),
    first_name: String(row.first_name ?? ''),
    last_name: String(row.last_name ?? ''),
    email: String(row.email ?? ''),
    assigned_at: String(row.assigned_at),
  }
}

function buildFullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim()
}

function isAuthBoundaryError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const maybeError = error as { message?: unknown; status?: unknown }
  const message = String(maybeError.message ?? '')
  const status = Number(maybeError.status)

  return (
    status === 401 ||
    status === 403 ||
    message.includes('Auth session missing') ||
    message.includes('JWT expired') ||
    message.includes('Invalid JWT')
  )
}

async function extractFunctionErrorCode(error: unknown): Promise<string | null> {
  if (!error || typeof error !== 'object' || !('context' in error)) {
    return null
  }

  const context = (error as { context?: unknown }).context
  if (!(context instanceof Response)) {
    return null
  }

  try {
    const payload = (await context.clone().json()) as { code?: unknown } | null
    if (payload && typeof payload === 'object' && payload.code) {
      return String(payload.code)
    }
  } catch {
    return null
  }

  return null
}

export async function getPublicSessions(from?: string, to?: string): Promise<PublicSession[]> {
  const { data, error } = await supabase.rpc('get_public_sessions', {
    p_from: from ?? new Date().toISOString(),
    p_to: to ?? new Date(Date.now() + 1000 * 60 * 60 * 24 * 90).toISOString(),
  })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as PublicSession[]
}

export async function getPublicCourseCatalog(): Promise<PublicCourseCatalogCourse[]> {
  const { data, error } = await supabase
    .from('courses_view')
    .select('id, name, description, level, color, duration_minutes, image_url, trainer_names')
    .order('name', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  return (((data as unknown) ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    name: String(row.name),
    description: (row.description as string | null) ?? null,
    level: (row.level as CourseLevel) ?? null,
    color: String(row.color ?? '#111827'),
    duration_minutes: Number(row.duration_minutes ?? 0),
    image_url: (row.image_url as string | null) ?? null,
    trainer_names: (row.trainer_names as string | null) ?? null,
  }))
}

export async function getPublicSessionById(sessionId: string): Promise<PublicSession> {
  const { data, error } = await supabase.rpc('get_public_session_by_id', {
    p_session_id: sessionId,
  })

  if (error) {
    throw normalizeError(error)
  }

  return one((data ?? []) as PublicSession[])
}

export async function memberHasBookingForSession(sessionId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('member_has_booking_for_session', {
    p_session_id: sessionId,
  })

  if (error) {
    throw normalizeError(error)
  }

  return Boolean(data)
}

export async function memberHasBookingsForSessions(
  sessionIds: string[],
): Promise<Record<string, boolean>> {
  if (sessionIds.length === 0) {
    return {}
  }

  const { data, error } = await supabase.rpc('member_has_bookings_for_sessions', {
    p_session_ids: sessionIds,
  })

  if (error) {
    throw normalizeError(error)
  }

  const bySessionId: Record<string, boolean> = Object.fromEntries(
    sessionIds.map((sessionId) => [sessionId, false]),
  )

  for (const row of (data ?? []) as { session_id: string; has_booking: boolean }[]) {
    bySessionId[row.session_id] = Boolean(row.has_booking)
  }

  return bySessionId
}

export async function getSubscriptionPackages(): Promise<SubscriptionPackage[]> {
  const { data, error } = await (supabase.rpc as Function)('get_member_subscription_packages')

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    type: String(row.type) as SubscriptionPackage['type'],
    name: String(row.name),
    price_cents: Number(row.price_cents ?? 0),
    discount_price_cents:
      row.discount_price_cents == null ? null : Number(row.discount_price_cents),
    credits_included: Number(row.credits_included ?? 0),
    max_sessions_per_week:
      row.max_sessions_per_week == null ? null : Number(row.max_sessions_per_week),
    billing_mode: String(row.billing_mode) as SubscriptionPackage['billing_mode'],
    is_active: Boolean(row.is_active),
    base_price_cents: Number(row.base_price_cents ?? row.price_cents ?? 0),
    effective_price_cents: Number(row.effective_price_cents ?? row.price_cents ?? 0),
    applied_discount_id: (row.applied_discount_id as string | null) ?? null,
    applied_discount_title: (row.applied_discount_title as string | null) ?? null,
    applied_discount_type:
      (row.applied_discount_type as SubscriptionPackage['applied_discount_type']) ?? null,
    applied_discount_percent:
      row.applied_discount_percent == null ? null : Number(row.applied_discount_percent),
    applied_discount_amount_cents: Number(row.applied_discount_amount_cents ?? 0),
    applied_stripe_coupon_id: (row.applied_stripe_coupon_id as string | null) ?? null,
  }))
}

export async function getUserSubscriptionStatus(): Promise<UserSubscription | null> {
  const { data, error } = await (supabase.rpc as Function)('get_user_subscription_status')

  if (error) {
    throw normalizeError(error)
  }

  const row = ((data ?? []) as Record<string, unknown>[])[0]
  return row ? mapUserSubscription(row) : null
}

export async function getUserPurchasedPackages(): Promise<UserPurchasedPackage[]> {
  const { data, error } = await (supabase.rpc as Function)('get_user_purchased_packages')

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    package_type: String(row.package_type) as UserPurchasedPackage['package_type'],
    package_name: String(row.package_name),
    billing_mode: String(row.billing_mode) as UserPurchasedPackage['billing_mode'],
    purchased_credits:
      row.purchased_credits == null ? null : Number(row.purchased_credits),
    debited_credits: Number(row.debited_credits ?? 0),
    remaining_credits: Number(row.remaining_credits ?? 0),
    expires_at: (row.expires_at as string | null) ?? null,
    is_active: Boolean(row.is_active),
    stripe_status: (row.stripe_status as string | null) ?? null,
    current_period_end: (row.current_period_end as string | null) ?? null,
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
    manual_reason: (row.manual_reason as string | null) ?? null,
    entry_kind:
      row.entry_kind === 'manual_debit'
        ? 'manual_debit'
        : row.entry_kind === 'voucher'
          ? 'voucher'
          : 'package',
  }))
}

export async function getUserPaymentEntries(): Promise<PaymentEntry[]> {
  const { data, error } = await (supabase.rpc as Function)('get_user_payment_entries')

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    package_type: String(row.package_type) as PaymentEntry['package_type'],
    package_name: String(row.package_name),
    payment_kind: String(row.payment_kind) as PaymentEntry['payment_kind'],
    payment_provider: String(row.payment_provider) as PaymentEntry['payment_provider'],
    amount_paid_cents: Number(row.amount_paid_cents ?? 0),
    currency: String(row.currency ?? 'eur'),
    payment_status: String(row.payment_status ?? 'paid'),
    period_start: (row.period_start as string | null) ?? null,
    period_end: (row.period_end as string | null) ?? null,
    paid_at: (row.paid_at as string | null) ?? null,
    document_kind: (row.document_kind as PaymentEntry['document_kind']) ?? null,
    document_url: (row.document_url as string | null) ?? null,
    stripe_invoice_id: (row.stripe_invoice_id as string | null) ?? null,
    reference_number: (row.reference_number as string | null) ?? null,
    notes: (row.notes as string | null) ?? null,
    created_at: String(row.created_at),
  }))
}

export async function redeemVoucherCode(code: string): Promise<VoucherRedemptionResult> {
  const { data, error } = await callRpc<Record<string, unknown>[]>('redeem_voucher_code', {
    p_code: code,
  })

  if (error) {
    throw normalizeError(error)
  }

  const row = one((data ?? []) as Record<string, unknown>[])
  return mapVoucherRedemptionResult(row)
}

export async function getVoucherCodes(): Promise<VoucherCode[]> {
  const { data, error } = await callRpc<Record<string, unknown>[]>('get_voucher_codes')

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map(mapVoucherCode)
}

export async function generateVoucherCode(): Promise<string> {
  const { data, error } = await callRpc<string>('generate_voucher_code', {})

  if (error) {
    throw normalizeError(error)
  }

  return String(data ?? '')
}

export async function createVoucherCode(payload: VoucherCodeMutationInput): Promise<VoucherCode> {
  const { data, error } = await callRpc<Record<string, unknown>[]>('create_voucher_code', {
    p_credits: payload.credits,
    p_max_redemptions: payload.maxRedemptions,
    p_code: payload.code?.trim() || null,
  })

  if (error) {
    throw normalizeError(error)
  }

  const row = one((data ?? []) as Record<string, unknown>[])
  return mapVoucherCode(row)
}

export async function updateVoucherCode(payload: VoucherCodeUpdateInput): Promise<VoucherCode> {
  const { data, error } = await callRpc<Record<string, unknown>[]>('update_voucher_code', {
    p_voucher_id: payload.id,
    p_code: payload.code?.trim() || '',
    p_credits: payload.credits,
    p_max_redemptions: payload.maxRedemptions,
  })

  if (error) {
    throw normalizeError(error)
  }

  const row = one((data ?? []) as Record<string, unknown>[])
  return mapVoucherCode(row)
}

export async function deleteVoucherCode(voucherId: string): Promise<void> {
  const { error } = await callRpc<null>('delete_voucher_code', {
    p_voucher_id: voucherId,
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function getDiscounts(): Promise<Discount[]> {
  const { data, error } = await callRpc<Record<string, unknown>[]>('get_discounts')

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map(mapDiscount)
}

async function manageDiscountMutation(body: Record<string, unknown>): Promise<Discount> {
  const { data, error } = await supabase.functions.invoke('manage-discount', {
    body,
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  const result = data as { discount?: unknown; code?: unknown } | null
  if (result?.code) {
    throw new Error(String(result.code))
  }

  if (!result?.discount || typeof result.discount !== 'object') {
    throw new Error('DISCOUNT_MUTATION_FAILED')
  }

  return mapDiscount(result.discount as Record<string, unknown>)
}

export async function createDiscount(payload: DiscountMutationInput): Promise<Discount> {
  return manageDiscountMutation({
    action: 'create',
    title: payload.title,
    discountType: payload.discountType,
    percentOff: payload.discountType === 'percent' ? payload.percentOff : null,
    amountCents: payload.discountType === 'amount' ? payload.amountCents : null,
  })
}

export async function updateDiscount(payload: DiscountUpdateInput): Promise<Discount> {
  return manageDiscountMutation({
    action: 'update',
    discountId: payload.id,
    title: payload.title,
    discountType: payload.discountType,
    percentOff: payload.discountType === 'percent' ? payload.percentOff : null,
    amountCents: payload.discountType === 'amount' ? payload.amountCents : null,
    isActive: payload.isActive ?? true,
  })
}

export async function deleteDiscount(discountId: string): Promise<void> {
  await manageDiscountMutation({
    action: 'delete',
    discountId,
  })
}

export async function getMemberDiscountAssignments(
  userId: string,
): Promise<MemberDiscountAssignment[]> {
  const { data, error } = await callRpc<Record<string, unknown>[]>(
    'get_member_discount_assignments',
    {
      p_user_id: userId,
    },
  )

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map(mapMemberDiscountAssignment)
}

export async function getDiscountMemberAssignments(
  discountId: string,
): Promise<DiscountMemberAssignment[]> {
  const { data, error } = await callRpc<Record<string, unknown>[]>(
    'get_discount_member_assignments',
    {
      p_discount_id: discountId,
    },
  )

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map(mapDiscountMemberAssignment)
}

export async function manageMemberDiscount(payload: {
  userId: string
  discountId: string
  action: 'assign' | 'remove'
}): Promise<MemberDiscountAssignment[]> {
  const { data, error } = await supabase.functions.invoke('manage-member-discount', {
    body: payload,
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  const result = data as { assignments?: unknown; code?: unknown } | null
  if (!result) {
    throw new Error('DISCOUNT_ASSIGNMENT_FAILED')
  }

  if (result.code) {
    throw new Error(String(result.code))
  }

  return ((result.assignments ?? []) as Record<string, unknown>[]).map(mapMemberDiscountAssignment)
}

export async function getManagedUserProfile(userId: string): Promise<Profile> {
  const { data, error } = await (supabase.rpc as Function)('get_managed_user_profile', {
    p_user_id: userId,
  })

  if (error) {
    throw normalizeError(error)
  }

  const row = one((data ?? []) as Record<string, unknown>[])
  return {
    id: String(row.id),
    role: String(row.role) as UserRole,
    first_name: String(row.first_name),
    last_name: String(row.last_name),
    full_name: (row.full_name as string | null) ?? null,
    email: (row.email as string | null) ?? '',
    is_active: Boolean(row.is_active),
    birth_date: (row.birth_date as string | null) ?? null,
    street: (row.street as string | null) ?? null,
    house_number: (row.house_number as string | null) ?? null,
    postal_code: (row.postal_code as string | null) ?? null,
    city: (row.city as string | null) ?? null,
    phone_number: (row.phone_number as string | null) ?? null,
    company: (row.company as string | null) ?? null,
    iban: (row.iban as string | null) ?? null,
    bank_name: (row.bank_name as string | null) ?? null,
    avatar_url: (row.avatar_url as string | null) ?? null,
    created_at: String(row.created_at || ''),
  }
}

export async function getManagedUserBookings(userId: string): Promise<ManagedUserBooking[]> {
  const { data, error } = await (supabase.rpc as Function)('get_managed_user_bookings', {
    p_user_id: userId,
  })

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    booking_id: String(row.booking_id),
    session_id: String(row.session_id),
    status: String(row.status) as BookingStatus,
    created_at: String(row.created_at),
    confirmed_at: (row.confirmed_at as string | null) ?? null,
    cancelled_at: (row.cancelled_at as string | null) ?? null,
    consumed_credit: Boolean(row.consumed_credit),
    booking_source:
      String(row.booking_source) === 'credit'
        ? 'credit'
        : 'subscription',
    course_name: String(row.course_name),
    course_level: (row.course_level as string | null) ?? null,
    starts_at: String(row.starts_at),
    ends_at: String(row.ends_at),
    registration_until: (row.registration_until as string | null) ?? null,
    cancellation_until: (row.cancellation_until as string | null) ?? null,
    max_participants: Number(row.max_participants),
    min_participants: row.min_participants == null ? null : Number(row.min_participants),
    trainer_id: (row.trainer_id as string | null) ?? null,
    trainer_name: (row.trainer_name as string | null) ?? null,
    resource_name: (row.resource_name as string | null) ?? null,
  }))
}

export async function getSessionCancellationSettings(): Promise<SessionCancellationSettings> {
  const { data, error } = await (supabase.rpc as Function)('get_session_cancellation_hours')

  if (error) {
    throw normalizeError(error)
  }

  return {
    hours: Math.max(0, Number(data ?? 12)),
  }
}

export async function getSessionRegistrationSettings(): Promise<SessionRegistrationSettings> {
  const { data, error } = await (supabase.rpc as Function)('get_session_registration_hours')

  if (error) {
    throw normalizeError(error)
  }

  return {
    hours: Math.max(0, Number(data ?? 0)),
  }
}

export async function updateSessionRegistrationSettings(
  hours: number,
): Promise<SessionRegistrationSettings> {
  const { data, error } = await (supabase.rpc as Function)('set_session_registration_hours', {
    p_hours: Math.max(0, Math.trunc(hours)),
  })

  if (error) {
    throw normalizeError(error)
  }

  return {
    hours: Math.max(0, Number(data ?? 0)),
  }
}

export async function updateSessionCancellationSettings(
  hours: number,
): Promise<SessionCancellationSettings> {
  const { data, error } = await (supabase.rpc as Function)('set_session_cancellation_hours', {
    p_hours: Math.max(0, Math.trunc(hours)),
  })

  if (error) {
    throw normalizeError(error)
  }

  return {
    hours: Math.max(0, Number(data ?? 12)),
  }
}

export async function getManagedUserPackages(userId: string): Promise<ManagedUserPackage[]> {
  const { data, error } = await (supabase.rpc as Function)('get_managed_user_packages', {
    p_user_id: userId,
  })

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    package_type: String(row.package_type) as ManagedUserPackage['package_type'],
    package_name: String(row.package_name),
    billing_mode: String(row.billing_mode) as ManagedUserPackage['billing_mode'],
    purchased_credits:
      row.purchased_credits == null ? null : Number(row.purchased_credits),
    debited_credits: Number(row.debited_credits ?? 0),
    remaining_credits: Number(row.remaining_credits ?? 0),
    expires_at: (row.expires_at as string | null) ?? null,
    is_active: Boolean(row.is_active),
    stripe_status: (row.stripe_status as string | null) ?? null,
    current_period_end: (row.current_period_end as string | null) ?? null,
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
    manual_reason: (row.manual_reason as string | null) ?? null,
    entry_kind:
      String(row.entry_kind) === 'manual_debit'
        ? 'manual_debit'
        : String(row.entry_kind) === 'voucher'
          ? 'voucher'
          : 'package',
  }))
}

export async function getManagedUserPaymentEntries(
  userId: string,
): Promise<ManagedUserPaymentEntry[]> {
  const { data, error } = await (supabase.rpc as Function)(
    'get_managed_user_payment_entries',
    {
      p_user_id: userId,
    },
  )

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    package_type: String(row.package_type) as ManagedUserPaymentEntry['package_type'],
    package_name: String(row.package_name),
    payment_kind: String(row.payment_kind) as ManagedUserPaymentEntry['payment_kind'],
    payment_provider: String(row.payment_provider) as ManagedUserPaymentEntry['payment_provider'],
    amount_paid_cents: Number(row.amount_paid_cents ?? 0),
    currency: String(row.currency ?? 'eur'),
    payment_status: String(row.payment_status ?? 'paid'),
    period_start: (row.period_start as string | null) ?? null,
    period_end: (row.period_end as string | null) ?? null,
    paid_at: (row.paid_at as string | null) ?? null,
    document_kind: (row.document_kind as ManagedUserPaymentEntry['document_kind']) ?? null,
    document_url: (row.document_url as string | null) ?? null,
    stripe_invoice_id: (row.stripe_invoice_id as string | null) ?? null,
    reference_number: (row.reference_number as string | null) ?? null,
    notes: (row.notes as string | null) ?? null,
    created_at: String(row.created_at),
  }))
}

export async function getManagedUserSubscriptionStatus(
  userId: string,
): Promise<UserSubscription | null> {
  const { data, error } = await (supabase.rpc as Function)('get_managed_user_subscription_status', {
    p_user_id: userId,
  })

  if (error) {
    throw normalizeError(error)
  }

  const row = ((data ?? []) as Record<string, unknown>[])[0]
  return row ? mapUserSubscription(row) : null
}

export async function adjustManagedUserCredits(payload: {
  userId: string
  delta: number
  reason?: string
  amountPaidCents?: number | null
  operationKey?: string
}): Promise<{ total_credits: number }> {
  if (payload.delta > 0 && (payload.amountPaidCents ?? 0) > 0) {
    const operationKey = payload.operationKey ?? crypto.randomUUID()
    const { data, error } = await supabase.functions.invoke('create-manual-credit-invoice', {
      body: {
        userId: payload.userId,
        credits: payload.delta,
        reason: payload.reason ?? null,
        amountPaidCents: payload.amountPaidCents ?? null,
        operationKey,
      },
    })

    if (error) {
      const functionErrorCode = await extractFunctionErrorCode(error)
      if (functionErrorCode) {
        throw new Error(functionErrorCode)
      }
      throw normalizeError(error)
    }

    const result = data as { totalCredits?: unknown; code?: unknown } | null
    if (!result) {
      throw new Error('PAYMENT_ENTRY_CREATE_FAILED')
    }

    if (result.code) {
      throw new Error(String(result.code))
    }

    return {
      total_credits: Number(result.totalCredits ?? 0),
    }
  }

  const { data, error } = await (supabase.rpc as Function)('adjust_managed_user_credits', {
    p_user_id: payload.userId,
    p_delta: payload.delta,
    p_reason: payload.reason ?? null,
    p_amount_paid_cents: payload.amountPaidCents ?? null,
  })

  if (error) {
    throw normalizeError(error)
  }

  const row = ((data ?? []) as { total_credits?: number }[])[0]
  return {
    total_credits: Number(row?.total_credits ?? 0),
  }
}

export async function getManagedUserCreditAdjustments(
  userId: string,
  options?: { limit?: number; offset?: number },
): Promise<ManagedUserCreditAdjustment[]> {
  const { data, error } = await (supabase.rpc as Function)('get_managed_user_credit_adjustments', {
    p_user_id: userId,
    p_limit: options?.limit ?? 200,
    p_offset: options?.offset ?? 0,
  })

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    user_id: String(row.user_id),
    actor_user_id: (row.actor_user_id as string | null) ?? null,
    actor_name: String(row.actor_name ?? 'Unbekannt'),
    delta: Number(row.delta),
    reason: String(row.reason ?? ''),
    total_before: Number(row.total_before ?? 0),
    total_after: Number(row.total_after ?? 0),
    bucket_subscription_id: (row.bucket_subscription_id as string | null) ?? null,
    created_at: String(row.created_at),
  }))
}

export async function bookManagedUserSession(payload: {
  userId: string
  sessionId: string
}): Promise<void> {
  const { error } = await (supabase.rpc as Function)('book_managed_user_session', {
    p_user_id: payload.userId,
    p_session_id: payload.sessionId,
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function cancelManagedUserBooking(payload: {
  userId: string
  bookingId: string
}): Promise<void> {
  const { error } = await (supabase.rpc as Function)('cancel_managed_user_booking', {
    p_user_id: payload.userId,
    p_booking_id: payload.bookingId,
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function bookSessionWithCredit(sessionId: string): Promise<void> {
  const { error } = await (supabase.rpc as Function)('book_session_with_credit', {
    p_session_id: sessionId,
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function createSubscriptionCheckoutSession(packageType: string): Promise<{
  sessionId: string
  url: string
}> {
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { packageType },
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  const payload = data as { sessionId?: unknown; url?: unknown; code?: unknown } | null
  if (!payload) {
    throw new Error('CHECKOUT_SESSION_CREATE_FAILED')
  }

  if (payload.code) {
    throw new Error(String(payload.code))
  }

  const sessionId = String(payload.sessionId ?? '')
  const url = String(payload.url ?? '')

  if (!sessionId || !url) {
    throw new Error('CHECKOUT_SESSION_CREATE_FAILED')
  }

  return { sessionId, url }
}

export async function createCustomerPortalSession(): Promise<{ url: string }> {
  const { data, error } = await supabase.functions.invoke('create-customer-portal-session', {
    body: {},
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  const payload = data as { url?: unknown; code?: unknown } | null
  if (!payload) {
    throw new Error('CUSTOMER_PORTAL_CREATE_FAILED')
  }

  if (payload.code) {
    throw new Error(String(payload.code))
  }

  const url = String(payload.url ?? '')
  if (!url) {
    throw new Error('CUSTOMER_PORTAL_CREATE_FAILED')
  }

  return { url }
}

export async function createInvoiceDownloadUrl(stripeInvoiceId: string): Promise<{ url: string }> {
  const { data, error } = await supabase.functions.invoke('create-invoice-download-url', {
    body: { stripeInvoiceId },
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  const payload = data as { url?: unknown; code?: unknown } | null
  if (!payload) {
    throw new Error('INVOICE_DOWNLOAD_FAILED')
  }

  if (payload.code) {
    throw new Error(String(payload.code))
  }

  const url = String(payload.url ?? '')
  if (!url) {
    throw new Error('INVOICE_DOWNLOAD_FAILED')
  }

  return { url }
}

export async function getMemberBookings(params: {
  upcomingOnly?: boolean
}): Promise<Booking[]> {
  const { data, error } = await supabase.rpc('get_member_bookings', {
    p_upcoming_only: params.upcomingOnly ?? false,
  })

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => {
    const hasSessionDetails =
      row.starts_at != null &&
      row.ends_at != null &&
      row.max_participants != null

    return {
      id: String(row.booking_id),
      session_id: String(row.session_id),
      full_name: String(row.full_name ?? '-'),
      email: String(row.email ?? '-'),
      status: row.status as BookingStatus,
      created_at: String(row.created_at),
      confirmed_at: (row.confirmed_at as string | null) ?? null,
      cancelled_at: (row.cancelled_at as string | null) ?? null,
      course_sessions: hasSessionDetails
        ? {
          id: String(row.session_id),
          starts_at: String(row.starts_at),
          ends_at: String(row.ends_at),
          max_participants: Number(row.max_participants ?? 0),
          min_participants: (row.min_participants as number | null) ?? null,
          confirmed_count: Number(row.confirmed_count ?? 0),
          registration_until: (row.registration_until as string | null) ?? null,
          cancellation_until: (row.cancellation_until as string | null) ?? null,
          trainer_id: row.trainer_id ? String(row.trainer_id) : null,
          trainer_name: row.trainer_name ? String(row.trainer_name) : null,
          resource_id: (row.resource_id as string | null) ?? null,
          resource_name: row.resource_name ? String(row.resource_name) : null,
          courses: row.course_name
            ? {
              name: String(row.course_name),
              description: (row.course_description as string | null) ?? null,
              level: (row.course_level as string | null) ?? null,
            }
            : undefined,
        }
        : undefined,
    }
  })
}

export async function cancelMemberBooking(bookingId: string): Promise<void> {
  const { error } = await (supabase.rpc as Function)('cancel_member_booking', {
    p_booking_id: bookingId,
  })
  if (error) {
    throw normalizeError(error)
  }
}

export async function signInUser(payload: {
  email: string
  password: string
  captchaToken?: string
}): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({
    email: payload.email,
    password: payload.password,
    ...(payload.captchaToken
      ? {
        options: {
          captchaToken: payload.captchaToken,
        },
      }
      : {}),
  })
  if (error) {
    throw normalizeError(error)
  }
}

export async function signUpUser(payload: {
  email: string
  password: string
  firstName: string
  lastName: string
  phoneNumber: string
  company?: string
  captchaToken?: string
}): Promise<void> {
  const fullName = buildFullName(payload.firstName, payload.lastName)
  const phoneNumber = payload.phoneNumber.trim()
  const company = payload.company?.trim() || undefined

  if (!phoneNumber) {
    throw new Error('Bitte gib deine Telefonnummer ein.')
  }

  const { error } = await supabase.auth.signUp({
    email: payload.email,
    password: payload.password,
    options: {
      data: {
        first_name: payload.firstName,
        last_name: payload.lastName,
        full_name: fullName,
        phone_number: phoneNumber,
        company,
      },
      emailRedirectTo: `${env.appUrl}/login`,
      ...(payload.captchaToken ? { captchaToken: payload.captchaToken } : {}),
    },
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function signOutUser(): Promise<void> {
  const { error } = await supabase.auth.signOut()
  if (error) {
    throw normalizeError(error)
  }
}

export async function sendPasswordResetEmail(email: string, captchaToken?: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${env.appUrl}/reset-passwort`,
    ...(captchaToken ? { captchaToken } : {}),
  })
  if (error) {
    throw normalizeError(error)
  }
}

export async function updateUserPassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    password: password,
  })
  if (error) {
    throw normalizeError(error)
  }
}

export const signInAdmin = signInUser
export const signOutAdmin = signOutUser

export async function getCurrentUserRole(): Promise<Profile | null> {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession()

  if (sessionError) {
    if (isAuthBoundaryError(sessionError)) {
      return null
    }
    throw normalizeError(sessionError)
  }

  const user = session?.user
  if (!user) {
    return null
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, first_name, last_name, full_name, email, is_active, birth_date, street, house_number, postal_code, city, phone_number, company, avatar_url')
    .eq('id', user.id)
    .maybeSingle()

  if (error) {
    if (isAuthBoundaryError(error)) {
      return null
    }
    throw normalizeError(error)
  }

  return (data as Profile | null) ?? null
}

export async function getCourses(includeInactive = true): Promise<Course[]> {
  const currentUser = await getCurrentUserRole()
  const isAdmin = currentUser?.role === 'admin'
  const courseSelect = isAdmin
    ? `
      id,
      name,
      description,
      level,
      color,
      duration_minutes,
      default_max_participants,
      default_min_participants,
      is_active,
      image_url,
      created_at,
      updated_at,
      course_trainers(
        trainer:profiles!course_trainers_trainer_id_fkey(
          id,
          role,
          full_name,
          email,
          is_active
        )
      )
    `
    : `
      id,
      name,
      description,
      level,
      color,
      duration_minutes,
      default_max_participants,
      default_min_participants,
      is_active,
      image_url,
      created_at,
      updated_at,
      course_trainers(
        trainer_id
      )
    `

  let query = supabase
    .from('courses')
    .select(courseSelect)
    .order('created_at', { ascending: false })

  if (!includeInactive) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query

  if (error) {
    throw normalizeError(error)
  }

  return (((data as unknown) ?? []) as Record<string, unknown>[]).map((row) => {
    const courseTrainerRel = row.course_trainers
    const courseTrainers = Array.isArray(courseTrainerRel)
      ? courseTrainerRel
      : courseTrainerRel
        ? [courseTrainerRel]
        : []

    const trainers = courseTrainers
      .map((courseTrainer) => {
        if (!isAdmin) {
          const trainerId = (courseTrainer as Record<string, unknown>).trainer_id
          if (!trainerId) {
            return null
          }

          return {
            id: String(trainerId),
            role: 'trainer' as UserRole,
            full_name: null,
            email: '',
            is_active: true,
          }
        }

        const trainerRel = (courseTrainer as Record<string, unknown>).trainer
        const trainer = Array.isArray(trainerRel) ? trainerRel[0] : trainerRel
        if (!trainer) {
          return null
        }

        return {
          id: String((trainer as Record<string, unknown>).id),
          role: String((trainer as Record<string, unknown>).role) as UserRole,
          full_name: ((trainer as Record<string, unknown>).full_name as string | null) ?? null,
          email: String((trainer as Record<string, unknown>).email),
          is_active: Boolean((trainer as Record<string, unknown>).is_active),
        }
      })
      .filter((trainer): trainer is NonNullable<typeof trainer> => trainer !== null)

    return {
      id: String(row.id),
      name: String(row.name),
      description: (row.description as string | null) ?? null,
      level: (row.level as CourseLevel),
      color: String(row.color),
      duration_minutes: Number(row.duration_minutes),
      default_max_participants: Number(row.default_max_participants),
      default_min_participants:
        row.default_min_participants == null ? null : Number(row.default_min_participants),
      is_active: Boolean(row.is_active),
      image_url: (row.image_url as string | null) ?? null,
      created_at: String(row.created_at),
      updated_at: String(row.updated_at),
      trainers,
    }
  })
}

export async function getProfilesPaginated(params: {
  roles: UserRole[]
  searchTerm: string
  roleFilter: 'all' | UserRole
  statusFilter: UserStatusFilter
  sortKey: UserListSortKey
  sortDirection: SortDirection
  page: number
  pageSize: number
}, options: ProfilesPaginatedOptions = {}): Promise<PaginatedResult<Profile>> {
  const { roles, roleFilter, statusFilter, sortKey, sortDirection } = params
  const hideContactData = options.hideContactData === true
  const searchPattern = toSearchPattern(params.searchTerm)
  const normalizedSearchTerm = params.searchTerm.trim().toLowerCase()
  const { from, to } = toPaginationRange(params.page, params.pageSize)
  const ascending = sortDirection === 'asc'
  const effectiveSortKey = hideContactData && sortKey === 'email' ? 'name' : sortKey
  const selectFields = hideContactData
    ? 'id, role, first_name, last_name, full_name, is_active, birth_date, company, avatar_url, created_at'
    : 'id, role, first_name, last_name, full_name, email, is_active, birth_date, street, house_number, postal_code, city, phone_number, company, avatar_url, created_at'

  let query = (supabase.from('profiles') as any)
    .select(selectFields, { count: 'exact' })
    .in('role', roles)

  if (roleFilter !== 'all') {
    query = query.eq('role', roleFilter)
  }

  if (statusFilter === 'active') {
    query = query.eq('is_active', true)
  } else if (statusFilter === 'inactive') {
    query = query.eq('is_active', false)
  }

  if (searchPattern) {
    const orConditions = [
      `first_name.ilike.${searchPattern}`,
      `last_name.ilike.${searchPattern}`,
      `full_name.ilike.${searchPattern}`,
      `company.ilike.${searchPattern}`,
    ]
    if (!hideContactData) {
      orConditions.push(`email.ilike.${searchPattern}`)
    }
    if (normalizedSearchTerm.includes('admin')) {
      orConditions.push('role.eq.admin')
    }
    if (normalizedSearchTerm.includes('trainer')) {
      orConditions.push('role.eq.trainer')
    }
    if (normalizedSearchTerm.includes('teilnehmer') || normalizedSearchTerm.includes('member')) {
      orConditions.push('role.eq.member')
    }
    if (
      normalizedSearchTerm.includes('deaktiv') ||
      normalizedSearchTerm.includes('inaktiv')
    ) {
      orConditions.push('is_active.eq.false')
    } else if (normalizedSearchTerm.includes('aktiv')) {
      orConditions.push('is_active.eq.true')
    }

    query = query.or(orConditions.join(','))
  }

  switch (effectiveSortKey) {
    case 'email':
      query = query.order('email', { ascending })
      break
    case 'role':
      query = query.order('role', { ascending })
      break
    case 'status':
      query = query.order('is_active', { ascending })
      break
    case 'created_at':
      query = query.order('created_at', { ascending })
      break
    case 'name':
    default:
      query = query.order('first_name', { ascending }).order('last_name', { ascending })
      break
  }

  query = (hideContactData ? query.order('id', { ascending: true }) : query.order('email', { ascending: true }))
    .range(from, to)

  const { data, error, count } = await query

  if (error) {
    throw normalizeError(error)
  }

  return {
    items: ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      id: String(row.id),
      role: String(row.role) as UserRole,
      first_name: String(row.first_name),
      last_name: String(row.last_name),
      full_name: (row.full_name as string | null) ?? null,
      email: (row.email as string | null) ?? '',
      is_active: Boolean(row.is_active),
      birth_date: (row.birth_date as string | null) ?? null,
      street: (row.street as string | null) ?? null,
      house_number: (row.house_number as string | null) ?? null,
      postal_code: (row.postal_code as string | null) ?? null,
      city: (row.city as string | null) ?? null,
      phone_number: (row.phone_number as string | null) ?? null,
      company: (row.company as string | null) ?? null,
      avatar_url: (row.avatar_url as string | null) ?? null,
      created_at: String(row.created_at || ''),
    })),
    total: count ?? 0,
  }
}

export async function getTrainer(id: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, first_name, last_name, full_name, email, is_active, birth_date, street, house_number, postal_code, city, phone_number, company, avatar_url, created_at')
    .eq('id', id)
    .eq('role', 'trainer')
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw normalizeError(error)
  }

  return data as Profile
}

export async function getTrainerPublicSessions(
  trainerId: string,
  from: Date,
  to: Date
): Promise<PublicSession[]> {
  const { data, error } = await (supabase.rpc as Function)('get_trainer_public_sessions', {
    p_trainer_id: trainerId,
    p_from: from.toISOString(),
    p_to: to.toISOString(),
  })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as PublicSession[]
}

export async function getTrainerCourses(trainerId: string): Promise<TrainerCourse[]> {
  const { data, error } = await (supabase.rpc as Function)('get_trainer_courses', {
    p_trainer_id: trainerId,
  })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as TrainerCourse[]
}

export async function getTrainerProfiles(): Promise<Profile[]> {
  const { data, error } = await (supabase.rpc as Function)('get_visible_trainers')

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    role: 'trainer' as UserRole,
    first_name: String(row.first_name),
    last_name: String(row.last_name),
    full_name: (row.full_name as string | null) ?? null,
    email: '',
    is_active: true,
    birth_date: null,
    street: null,
    house_number: null,
    postal_code: null,
    city: null,
    phone_number: null,
    company: null,
    avatar_url: null,
    created_at: '',
  }))
}

export async function getStaffProfiles(
  params: AdminProfilesQueryParams,
): Promise<PaginatedResult<Profile>> {
  return getProfilesPaginated({
    ...params,
    roles: ['trainer', 'admin'],
  })
}

export async function getCustomerProfiles(
  params: AdminProfilesQueryParams,
): Promise<PaginatedResult<Profile>> {
  const { data, error } = await (supabase.rpc as Function)(
    'get_manageable_member_profiles_paginated',
    {
      p_search_term: params.searchTerm,
      p_role_filter: params.roleFilter,
      p_status_filter: params.statusFilter,
      p_sort_key: params.sortKey,
      p_sort_direction: params.sortDirection,
      p_page: params.page,
      p_page_size: params.pageSize,
    },
  )

  if (error) {
    throw normalizeError(error)
  }

  const rows = (data ?? []) as Record<string, unknown>[]

  return {
    items: rows.map((row) => ({
      id: String(row.id),
      role: String(row.role) as UserRole,
      first_name: String(row.first_name),
      last_name: String(row.last_name),
      full_name: (row.full_name as string | null) ?? null,
      email: (row.email as string | null) ?? '',
      is_active: Boolean(row.is_active),
      birth_date: (row.birth_date as string | null) ?? null,
      street: (row.street as string | null) ?? null,
      house_number: (row.house_number as string | null) ?? null,
      postal_code: (row.postal_code as string | null) ?? null,
      city: (row.city as string | null) ?? null,
      phone_number: (row.phone_number as string | null) ?? null,
      company: (row.company as string | null) ?? null,
      avatar_url: (row.avatar_url as string | null) ?? null,
      created_at: String(row.created_at || ''),
    })),
    total: rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0,
  }
}

export async function upsertProfile(profile: Profile): Promise<void> {
  const { error } = await supabase.from('profiles').upsert(profile)
  if (error) {
    throw normalizeError(error)
  }
}

export async function updateProfileByAdmin(
  id: string,
  payload: {
    email?: string
    first_name: string
    last_name: string
    role: UserRole
    is_active: boolean
    birth_date?: string | null
    street?: string | null
    house_number?: string | null
    postal_code?: string | null
    city?: string | null
    phone_number?: string | null
    company?: string | null
    iban?: string | null
    bank_name?: string | null
    avatar_url?: string | null
  },
): Promise<void> {
  const fullName = buildFullName(payload.first_name, payload.last_name)

  const { data, error } = await supabase.functions.invoke('admin-create-user', {
    body: {
      operation: 'update',
      userId: id,
      email: payload.email,
      firstName: payload.first_name,
      lastName: payload.last_name,
      fullName,
      role: payload.role,
      isActive: payload.is_active,
      birthDate: payload.birth_date,
      street: payload.street,
      houseNumber: payload.house_number,
      postalCode: payload.postal_code,
      city: payload.city,
      phoneNumber: payload.phone_number,
      company: payload.company,
      iban: payload.iban,
      bankName: payload.bank_name,
      avatarUrl: payload.avatar_url,
    },
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  if (data && typeof data === 'object' && 'code' in data) {
    throw new Error(String((data as { code?: unknown }).code ?? 'USER_UPDATE_FAILED'))
  }
}

export async function createUserByAdmin(payload: {
  email: string
  first_name: string
  last_name: string
  role: UserRole
  createMode: 'invite' | 'password'
  password?: string
  birth_date?: string | null
  street?: string | null
  house_number?: string | null
  postal_code?: string | null
  city?: string | null
  phone_number?: string | null
  company?: string | null
  iban?: string | null
  bank_name?: string | null
  avatar_url?: string | null
}): Promise<void> {
  const fullName = buildFullName(payload.first_name, payload.last_name)

  const { data, error } = await supabase.functions.invoke('admin-create-user', {
    body: {
      email: payload.email,
      firstName: payload.first_name,
      lastName: payload.last_name,
      fullName,
      role: payload.role,
      createMode: payload.createMode,
      password: payload.password,
      redirectTo: `${env.appUrl}/login`,
      birthDate: payload.birth_date,
      street: payload.street,
      houseNumber: payload.house_number,
      postalCode: payload.postal_code,
      city: payload.city,
      phoneNumber: payload.phone_number,
      company: payload.company,
      iban: payload.iban,
      bankName: payload.bank_name,
      avatarUrl: payload.avatar_url,
    },
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  if (data && typeof data === 'object' && 'code' in data) {
    throw new Error(String((data as { code?: unknown }).code ?? 'USER_CREATION_FAILED'))
  }
}

export async function deleteUserByAdmin(id: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('admin-create-user', {
    body: {
      operation: 'delete',
      userId: id,
    },
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }

  if (data && typeof data === 'object' && 'code' in data) {
    throw new Error(String((data as { code?: unknown }).code ?? 'USER_DELETION_FAILED'))
  }
}

export async function upsertAdminProfileByEmail(payload: {
  email: string
  fullName: string | null
}): Promise<void> {
  const { error } = await supabase.rpc('upsert_admin_profile_by_email', {
    p_email: payload.email,
    p_full_name: payload.fullName ?? undefined,
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function updateOwnProfile(payload: {
  first_name?: string
  last_name?: string
  birth_date?: string | null
  street?: string | null
  house_number?: string | null
  postal_code?: string | null
  city?: string | null
  phone_number?: string | null
  company?: string | null
  iban?: string | null
  bank_name?: string | null
  avatar_url?: string | null
}): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error('NOT_AUTHENTICATED')
  }

  const normalizedFirstName = payload.first_name?.trim()
  const normalizedLastName = payload.last_name?.trim()

  const { error } = await supabase
    .from('profiles')
    .update({
      first_name: normalizedFirstName,
      last_name: normalizedLastName,
      full_name:
        normalizedFirstName && normalizedLastName
          ? buildFullName(normalizedFirstName, normalizedLastName)
          : undefined,
      birth_date: payload.birth_date,
      street: payload.street,
      house_number: payload.house_number,
      postal_code: payload.postal_code,
      city: payload.city,
      phone_number: payload.phone_number,
      company: payload.company,
      iban: payload.iban,
      bank_name: payload.bank_name,
      avatar_url: payload.avatar_url,
    })
    .eq('id', session.user.id)

  if (error) {
    throw normalizeError(error)
  }
}

function normalizeIds(values: string[] | undefined): string[] {
  if (!values || values.length === 0) {
    return []
  }

  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)))
}

async function syncCourseTrainers(courseId: string, trainerIds: string[]): Promise<void> {
  const uniqueTrainerIds = normalizeIds(trainerIds)

  const { error: deleteError } = await supabase
    .from('course_trainers')
    .delete()
    .eq('course_id', courseId)

  if (deleteError) {
    throw normalizeError(deleteError)
  }

  if (uniqueTrainerIds.length === 0) {
    return
  }

  const { error: insertError } = await supabase.from('course_trainers').insert(
    uniqueTrainerIds.map((trainerId) => ({
      course_id: courseId,
      trainer_id: trainerId,
    })),
  )

  if (insertError) {
    throw normalizeError(insertError)
  }
}

export async function createCourse(payload: {
  name: string
  description: string
  level: CourseLevel
  color: string
  duration_minutes: number
  default_max_participants: number
  default_min_participants: number | null
  trainer_ids: string[]
  is_active: boolean
  image_url?: string | null
}): Promise<void> {
  const { trainer_ids, ...coursePayload } = payload

  const { data, error } = await supabase
    .from('courses')
    .insert(coursePayload as any)
    .select('id')
    .single()

  if (error) {
    throw normalizeError(error)
  }

  await syncCourseTrainers(String(data.id), trainer_ids)
}

export async function updateCourse(
  id: string,
  payload: {
    name: string
    description: string
    level: CourseLevel
    color: string
    duration_minutes: number
    default_max_participants: number
    default_min_participants: number | null
    trainer_ids: string[]
    is_active: boolean
    image_url?: string | null
  },
): Promise<void> {
  const { trainer_ids, ...coursePayload } = payload

  const { error } = await supabase.from('courses').update(coursePayload as any).eq('id', id)
  if (error) {
    throw normalizeError(error)
  }

  await syncCourseTrainers(id, trainer_ids)
}

export async function deleteOrDeactivateCourse(id: string): Promise<'deleted' | 'deactivated'> {
  const { data, error } = await callRpc<'deleted' | 'deactivated'>('admin_delete_or_deactivate_course', {
    p_course_id: id,
  })

  if (error) {
    throw normalizeError(error)
  }

  return data === 'deleted' ? 'deleted' : 'deactivated'
}

export async function saveWeekAsTemplate(startDate: string): Promise<number> {
  const { data, error } = await (supabase.rpc as any)('save_week_as_template', {
    p_start_date: startDate,
  })

  if (error) {
    throw normalizeError(error)
  }

  return Number(data ?? 0)
}

export async function applyTemplateToWeek(
  targetStartDate: string,
  overwrite: boolean = false,
): Promise<number> {
  const { data, error } = await (supabase.rpc as any)('apply_template_to_week', {
    p_target_start_date: targetStartDate,
    p_overwrite: overwrite,
  })

  if (error) {
    throw normalizeError(error)
  }

  return Number(data ?? 0)
}

export async function getSessions(range: { from: string; to: string }): Promise<CourseSession[]> {
  const { from, to } = range
  const currentUser = await getCurrentUserRole()
  const isTrainerOnly = currentUser?.role === 'trainer'
  const sessionSelect = isTrainerOnly
    ? 'id, course_id, trainer_id, resource_id, starts_at, ends_at, max_participants, min_participants, is_cancelled, registration_until, cancellation_until, created_at, updated_at, courses(id, name, description, level, color, duration_minutes, default_max_participants, default_min_participants), resource:resources(id, name), report:session_reports(actual_duration_minutes, trainer_notes, substitute_trainer_id)'
    : 'id, course_id, trainer_id, resource_id, starts_at, ends_at, max_participants, min_participants, is_cancelled, registration_until, cancellation_until, created_at, updated_at, courses(id, name, description, level, color, duration_minutes, default_max_participants, default_min_participants), trainer:profiles!course_sessions_trainer_id_fkey(id, full_name, email), resource:resources(id, name), report:session_reports(actual_duration_minutes, trainer_notes, substitute_trainer_id, substitute_trainer:profiles(id, full_name, email))'

  const { data, error } = await supabase
    .from('course_sessions')
    .select(sessionSelect)
    .lt('starts_at', to)
    .gt('ends_at', from)
    .order('starts_at', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as any[]).map((row) => {
    const courseRel = row.courses
    const course = Array.isArray(courseRel) ? courseRel[0] : courseRel
    const trainerRel = row.trainer
    const trainer = Array.isArray(trainerRel) ? trainerRel[0] : trainerRel
    const resourceRel = row.resource
    const resource = Array.isArray(resourceRel) ? resourceRel[0] : resourceRel

    return {
      id: String(row.id),
      course_id: String(row.course_id),
      trainer_id: (row.trainer_id as string | null) ?? null,
      resource_id: (row.resource_id as string | null) ?? null,
      starts_at: String(row.starts_at),
      ends_at: String(row.ends_at),
      max_participants: Number(row.max_participants),
      min_participants: row.min_participants == null ? null : Number(row.min_participants),
      is_cancelled: Boolean(row.is_cancelled),
      registration_until: (row.registration_until as string | null) ?? null,
      cancellation_until: (row.cancellation_until as string | null) ?? null,
      created_at: String(row.created_at),
      updated_at: String(row.updated_at),
      courses: course
        ? {
          id: String((course as Record<string, unknown>).id),
          name: String((course as Record<string, unknown>).name),
          description: ((course as Record<string, unknown>).description as string | null) ?? null,
          level: (course as Record<string, unknown>).level as CourseLevel,
          color: String((course as Record<string, unknown>).color),
          duration_minutes: Number((course as Record<string, unknown>).duration_minutes),
          default_max_participants: Number(
            (course as Record<string, unknown>).default_max_participants,
          ),
          default_min_participants:
            (course as Record<string, unknown>).default_min_participants == null
              ? null
              : Number((course as Record<string, unknown>).default_min_participants),
        }
        : undefined,
      trainer: trainer
        ? {
          id: String((trainer as Record<string, unknown>).id),
          full_name: ((trainer as Record<string, unknown>).full_name as string | null) ?? null,
          email: String((trainer as Record<string, unknown>).email),
        }
        : undefined,
      resource: resource
        ? {
          id: String((resource as Record<string, unknown>).id),
          name: String((resource as Record<string, unknown>).name),
        } as any
        : undefined,
      report: row.report
        ? {
          actual_duration_minutes: Number((row.report as Record<string, unknown>).actual_duration_minutes),
          trainer_notes: ((row.report as Record<string, unknown>).trainer_notes as string | null) ?? null,
          substitute_trainer_id:
            ((row.report as Record<string, unknown>).substitute_trainer_id as string | null) ?? null,
          substitute_trainer: (row.report as Record<string, unknown>).substitute_trainer
            ? {
              id: String(((row.report as Record<string, unknown>).substitute_trainer as Record<string, unknown>).id),
              full_name:
                (((row.report as Record<string, unknown>).substitute_trainer as Record<string, unknown>).full_name as string | null) ?? null,
              email: String(((row.report as Record<string, unknown>).substitute_trainer as Record<string, unknown>).email),
            }
            : undefined,
        }
        : undefined,
    }
  })
}

export async function createSession(payload: {
  course_id: string
  trainer_id: string | null
  resource_id: string | null
  starts_at: string
  ends_at: string
  max_participants: number
  min_participants: number | null
  is_cancelled: boolean
  registration_until: string | null
  cancellation_until: string | null
}): Promise<void> {
  const { error } = await supabase.from('course_sessions').insert(payload)
  if (error) {
    throw normalizeError(error)
  }
}

export async function updateSession(
  id: string,
  payload: {
    course_id: string
    trainer_id: string | null
    resource_id: string | null
    starts_at: string
    ends_at: string
    max_participants: number
    min_participants: number | null
    is_cancelled: boolean
    registration_until: string | null
    cancellation_until: string | null
  },
): Promise<void> {
  const { error } = await supabase.from('course_sessions').update(payload).eq('id', id)
  if (error) {
    throw normalizeError(error)
  }
}

export async function deleteSession(id: string): Promise<void> {
  const { error } = await supabase.from('course_sessions').delete().eq('id', id)
  if (error) {
    throw normalizeError(error)
  }
}

// Counts are aggregated in the database: selecting all booking rows would be truncated by
// PostgREST's max_rows (1000) and silently report 0 for sessions beyond that limit.
export async function getConfirmedBookingCountsBySession(
  sessionIds: string[],
): Promise<Record<string, number>> {
  if (sessionIds.length === 0) {
    return {}
  }

  const { data, error } = await supabase.rpc('get_confirmed_booking_counts', {
    p_session_ids: sessionIds,
  })

  if (error) {
    throw normalizeError(error)
  }

  // Every requested session gets an entry, so a missing key means "not loaded yet" instead of 0.
  const counts: Record<string, number> = Object.fromEntries(sessionIds.map((id) => [id, 0]))
  for (const row of (data ?? []) as { session_id: string; confirmed_count: number }[]) {
    counts[String(row.session_id)] = Number(row.confirmed_count)
  }

  return counts
}

async function getSessionIdsByCourseNameSearch(searchPattern: string): Promise<string[]> {
  const { data: matchingCourses, error: courseError } = await supabase
    .from('courses')
    .select('id')
    .ilike('name', searchPattern)

  if (courseError) {
    throw normalizeError(courseError)
  }

  const courseIds = ((matchingCourses ?? []) as Array<{ id: string | null }>)
    .map((course) => course.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)

  if (courseIds.length === 0) {
    return []
  }

  const { data: matchingSessions, error: sessionError } = await supabase
    .from('course_sessions')
    .select('id')
    .in('course_id', courseIds)

  if (sessionError) {
    throw normalizeError(sessionError)
  }

  return ((matchingSessions ?? []) as Array<{ id: string | null }>)
    .map((session) => session.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
}

export async function getBookings(
  params: AdminBookingsQueryParams,
): Promise<PaginatedResult<Booking>> {
  const { statusFilter, sortKey, sortDirection } = params
  const searchPattern = toSearchPattern(params.searchTerm)
  const normalizedSearchTerm = params.searchTerm.trim().toLowerCase()
  const { from, to } = toPaginationRange(params.page, params.pageSize)
  const ascending = sortDirection === 'asc'

  const isCourseFiltered = params.courseIdFilter && params.courseIdFilter !== 'all'
  const courseSessionsSelect = isCourseFiltered ? 'course_sessions!inner' : 'course_sessions'

  let query = supabase
    .from('bookings')
    .select(
      `id, session_id, full_name, email, status, created_at, confirmed_at, cancelled_at, ${courseSessionsSelect}(id, starts_at, ends_at, max_participants, min_participants, resource_id, courses(name, description), resource:resources(name))`,
      { count: 'exact' },
    )

  if (statusFilter !== 'all') {
    query = query.eq('status', statusFilter)
  }

  if (isCourseFiltered) {
    query = query.eq('course_sessions.course_id', params.courseIdFilter)
  }

  if (searchPattern) {
    const matchingSessionIds = await getSessionIdsByCourseNameSearch(searchPattern)
    const orConditions = [`full_name.ilike.${searchPattern}`, `email.ilike.${searchPattern}`]

    if (
      normalizedSearchTerm.includes('best') ||
      normalizedSearchTerm.includes('confirm')
    ) {
      orConditions.push('status.eq.confirmed')
    }
    if (
      normalizedSearchTerm.includes('storni') ||
      normalizedSearchTerm.includes('cancel')
    ) {
      orConditions.push('status.eq.cancelled')
    }
    if (normalizedSearchTerm === 'confirmed') {
      orConditions.push('status.eq.confirmed')
    }
    if (normalizedSearchTerm === 'cancelled') {
      orConditions.push('status.eq.cancelled')
    }
    if (matchingSessionIds.length > 0) {
      orConditions.push(`session_id.in.(${matchingSessionIds.join(',')})`)
    }

    query = query.or(orConditions.join(','))
  }

  switch (sortKey) {
    case 'name':
      query = query.order('full_name', { ascending })
      break
    case 'email':
      query = query.order('email', { ascending })
      break
    case 'status':
      query = query.order('status', { ascending })
      break
    case 'session':
      query = query.order('course_sessions(starts_at)', { ascending })
      break
    default:
      query = query.order('created_at', { ascending: false })
      break
  }

  query = query.order('created_at', { ascending: false }).range(from, to)

  const { data, error, count } = await query

  if (error) {
    throw normalizeError(error)
  }

  return {
    items: ((data ?? []) as Record<string, unknown>[]).map((row) => {
      const sessionRel = row.course_sessions
      const session = Array.isArray(sessionRel) ? sessionRel[0] : sessionRel
      const coursesRel = session ? (session as Record<string, unknown>).courses : undefined
      const course = Array.isArray(coursesRel) ? coursesRel[0] : coursesRel
      const resourceRel = session ? (session as Record<string, unknown>).resource : undefined
      const resource = Array.isArray(resourceRel) ? resourceRel[0] : resourceRel
      const fullName = (row.full_name as string | null) ?? null
      const email = (row.email as string | null) ?? null

      return {
        id: String(row.id),
        session_id: String(row.session_id),
        full_name: fullName && fullName.trim().length > 0 ? fullName : '-',
        email: email && email.trim().length > 0 ? email : '-',
        status: row.status as BookingStatus,
        created_at: String(row.created_at),
        confirmed_at: (row.confirmed_at as string | null) ?? null,
        cancelled_at: (row.cancelled_at as string | null) ?? null,
        course_sessions: session
          ? {
            id: String((session as Record<string, unknown>).id),
            starts_at: String((session as Record<string, unknown>).starts_at),
            ends_at: String((session as Record<string, unknown>).ends_at),
            max_participants: Number((session as Record<string, unknown>).max_participants ?? 0),
            min_participants: ((session as Record<string, unknown>).min_participants as number | null) ?? null,
            confirmed_count: 0, // Not fetched here, but we can default to 0
            registration_until: null,
            cancellation_until: null,
            trainer_id: null,
            trainer_name: null,
            resource_id: ((session as Record<string, unknown>).resource_id as string | null) ?? null,
            resource_name: resource ? String((resource as Record<string, unknown>).name) : null,
            courses: course
              ? {
                name: String((course as Record<string, unknown>).name),
                description: ((course as Record<string, unknown>).description as string | null) ?? null,
                level: null,
              }
              : undefined,
          }
          : undefined,
      }
    }),
    total: count ?? 0,
  }
}

export async function cancelBookingAsAdmin(id: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    throw normalizeError(error)
  }
}

export async function getTrainerSessions(
  trainerId: string,
  range: { from: string; to: string },
): Promise<CourseSession[]> {
  const { from, to } = range

  let query = supabase.from('course_sessions').select(`
      id,
      course_id,
      trainer_id,
      resource_id,
      starts_at,
      ends_at,
      max_participants,
      min_participants,
      is_cancelled,
      registration_until,
      cancellation_until,
      created_at,
      updated_at,
      courses(
        id,
        name,
        description,
        level,
        color,
        duration_minutes,
        default_max_participants,
        default_min_participants
      ),
      resource:resources(
        id,
        name
      ),
      report:session_reports(
        actual_duration_minutes,
        trainer_notes,
        substitute_trainer_id
      )
    `)

  query = query.eq('trainer_id', trainerId)

  const { data, error } = await query
    .lt('starts_at', to)
    .gt('ends_at', from)
    .order('starts_at', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  return ((data ?? []) as any[]).map((row) => {
    const courseRel = row.courses
    const course = Array.isArray(courseRel) ? courseRel[0] : courseRel
    const trainerRel = row.trainer
    const trainer = Array.isArray(trainerRel) ? trainerRel[0] : trainerRel
    const resourceRel = row.resource
    const resource = Array.isArray(resourceRel) ? resourceRel[0] : resourceRel

    return {
      id: String(row.id),
      course_id: String(row.course_id),
      trainer_id: (row.trainer_id as string | null) ?? null,
      resource_id: (row.resource_id as string | null) ?? null,
      starts_at: String(row.starts_at),
      ends_at: String(row.ends_at),
      max_participants: Number(row.max_participants),
      min_participants: row.min_participants == null ? null : Number(row.min_participants),
      is_cancelled: Boolean(row.is_cancelled),
      registration_until: (row.registration_until as string | null) ?? null,
      cancellation_until: (row.cancellation_until as string | null) ?? null,
      created_at: String(row.created_at),
      updated_at: String(row.updated_at),
      courses: course
        ? {
          id: String((course as Record<string, unknown>).id),
          name: String((course as Record<string, unknown>).name),
          description: ((course as Record<string, unknown>).description as string | null) ?? null,
          level: (course as Record<string, unknown>).level as CourseLevel,
          color: String((course as Record<string, unknown>).color),
          duration_minutes: Number((course as Record<string, unknown>).duration_minutes),
          default_max_participants: Number(
            (course as Record<string, unknown>).default_max_participants,
          ),
          default_min_participants:
            (course as Record<string, unknown>).default_min_participants == null
              ? null
              : Number((course as Record<string, unknown>).default_min_participants),
        }
        : undefined,
      trainer: trainer
        ? {
          id: String((trainer as Record<string, unknown>).id),
          full_name: ((trainer as Record<string, unknown>).full_name as string | null) ?? null,
          email: String((trainer as Record<string, unknown>).email),
        }
        : undefined,
      resource: resource
        ? {
          id: String((resource as Record<string, unknown>).id),
          name: String((resource as Record<string, unknown>).name),
        } as any
        : undefined,
      report: row.report
        ? {
          actual_duration_minutes: Number((row.report as Record<string, unknown>).actual_duration_minutes),
          trainer_notes: ((row.report as Record<string, unknown>).trainer_notes as string | null) ?? null,
          substitute_trainer_id:
            ((row.report as Record<string, unknown>).substitute_trainer_id as string | null) ?? null,
          substitute_trainer: (row.report as Record<string, unknown>).substitute_trainer
            ? {
              id: String(((row.report as Record<string, unknown>).substitute_trainer as Record<string, unknown>).id),
              full_name:
                (((row.report as Record<string, unknown>).substitute_trainer as Record<string, unknown>).full_name as string | null) ?? null,
              email: String(((row.report as Record<string, unknown>).substitute_trainer as Record<string, unknown>).email),
            }
            : undefined,
        }
        : undefined,
    }
  })
}

export async function getSessionBookings(sessionId: string): Promise<Booking[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []).map((row) => ({
    id: String(row.id),
    session_id: String(row.session_id),
    booked_by_user_id: (row.booked_by_user_id as string | null) ?? null,
    full_name: (row.full_name as string | null) ?? '-',
    email: (row.email as string | null) ?? '-',
    status: row.status as BookingStatus,
    created_at: String(row.created_at),
    confirmed_at: (row.confirmed_at as string | null) ?? null,
    cancelled_at: (row.cancelled_at as string | null) ?? null,
    consumed_credit: Boolean(row.consumed_credit),
    credit_subscription_id: (row.credit_subscription_id as string | null) ?? null,
  }))
}

export async function updateSessionReport(
  sessionId: string,
  payload: {
    actual_duration_minutes: number
    trainer_notes: string
    substitute_trainer_id: string | null
  },
): Promise<void> {
  const { error } = await supabase.from('session_reports').upsert({
    session_id: sessionId,
    ...payload,
    updated_at: new Date().toISOString(),
  })

  if (error) {
    throw normalizeError(error)
  }
}

export async function uploadAvatar(file: File, userId: string): Promise<string> {
  const fileExt = file.name.split('.').pop()
  const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`
  const filePath = `${userId}/${fileName}`

  // Upload to avatars bucket
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      upsert: true,
      cacheControl: '3600',
    })

  if (uploadError) {
    throw normalizeError(uploadError)
  }

  // Get public URL
  const { data } = supabase.storage.from('avatars').getPublicUrl(filePath)
  return data.publicUrl
}

export async function uploadCourseImage(file: File, courseId: string): Promise<string> {
  // Automatically optimize the image before upload (Resizes and converts to WebP)
  const optimizedFile = await optimizeCourseImage(file)
  const fileName = `${Math.random().toString(36).substring(2)}.webp`
  const filePath = `${courseId}/${fileName}`

  // Upload to course-images bucket
  const { error: uploadError } = await supabase.storage
    .from('course-images')
    .upload(filePath, optimizedFile, {
      upsert: true,
      cacheControl: '3600',
      contentType: 'image/webp',
    })

  if (uploadError) {
    throw normalizeError(uploadError)
  }

  // Get public URL
  const { data } = supabase.storage.from('course-images').getPublicUrl(filePath)
  return data.publicUrl
}

export async function getHonorariumTiers(trainerId: string | null): Promise<HonorariumTier[]> {
  const { data, error } = await supabase
    .from('honorarium_tiers')
    .select('*')
    .or(`trainer_id.eq.${trainerId},trainer_id.is.null`)
    .order('min_participants', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  const trainerTiers = (data ?? []).filter((t) => t.trainer_id === trainerId)
  return ((trainerTiers.length > 0 ? trainerTiers : data?.filter((t) => t.trainer_id === null)) ??
    []) as HonorariumTier[]
}

export async function updateHonorariumTiers(
  trainerId: string,
  tiers: Omit<HonorariumTier, 'id' | 'created_at' | 'updated_at'>[],
): Promise<void> {
  const { error: deleteError } = await supabase
    .from('honorarium_tiers')
    .delete()
    .eq('trainer_id', trainerId)

  if (deleteError) {
    throw normalizeError(deleteError)
  }

  if (tiers.length === 0) return

  const rows = tiers.map((tier) => ({
    trainer_id: trainerId,
    min_participants: tier.min_participants,
    max_participants: tier.max_participants,
    amount_cents: tier.amount_cents,
    is_default: false,
  }))

  const { error: insertError } = await supabase.from('honorarium_tiers').insert(rows)

  if (insertError) {
    throw normalizeError(insertError)
  }
}

export async function getBillableSessions(
  trainerId: string,
  startDate: Date,
  endDate: Date,
): Promise<(CourseSession & { booking_count: number; amount_cents: number })[]> {
  const rangeStart = startOfDay(startDate)
  const rangeEndExclusive = addDays(startOfDay(endDate), 1)

  const { data: sessions, error: sessionsError } = await supabase
    .from('course_sessions')
    .select(`
      *,
      courses(name, level),
      report:session_reports(actual_duration_minutes)
    `)
    .eq('trainer_id', trainerId)
    .gte('starts_at', rangeStart.toISOString())
    .lt('starts_at', rangeEndExclusive.toISOString())
    .lt('starts_at', new Date().toISOString())
    .eq('is_cancelled', false)
    .order('starts_at', { ascending: true })

  if (sessionsError) {
    throw normalizeError(sessionsError)
  }

  if (!sessions || sessions.length === 0) return []

  // Deduplicate sessions by ID just in case
  const uniqueSessions = Array.from(new Map(sessions.map((s) => [s.id, s])).values())
  const sessionIds = uniqueSessions.map((s) => s.id)
  // Get already billed session IDs (only from non-cancelled notes)
  const { data: billedItems, error: billedError } = await supabase
    .from('honorarium_note_items')
    .select(`
      session_id,
      honorarium_notes!inner(status)
    `)
    .in('session_id', sessionIds)
    .neq('honorarium_notes.status', 'cancelled')

  if (billedError) {
    throw normalizeError(billedError)
  }

  const billedSessionIds = new Set(billedItems?.map((i) => i.session_id) ?? [])

  const { data: counts, error: countsError } = await supabase.rpc('get_confirmed_booking_counts', {
    p_session_ids: sessionIds,
  })

  if (countsError) {
    throw normalizeError(countsError)
  }

  const countsMap = Object.fromEntries(
    (counts as { session_id: string; confirmed_count: number }[]).map((c) => [
      c.session_id,
      c.confirmed_count,
    ]),
  )

  const tiers = await getHonorariumTiers(trainerId)

  const calculateAmount = (count: number) => {
    const tier = tiers.find(
      (t) => count >= t.min_participants && (t.max_participants === null || count <= t.max_participants),
    )
    return tier ? tier.amount_cents : 0
  }

  return uniqueSessions.map((s) => {
    const count = countsMap[s.id] ?? 0
    return {
      ...s,
      is_billed: billedSessionIds.has(s.id),
      booking_count: count,
      amount_cents: calculateAmount(count),
    }
  }) as any
}

export async function createHonorariumNote(payload: {
  trainerId: string
  periodStart: Date
  periodEnd: Date
  items: { session_id: string; participant_count: number; amount_cents: number }[]
}): Promise<HonorariumNote> {
  // Deduplicate items by session_id to prevent double-counting
  const uniqueItems = Array.from(new Map(payload.items.map((item) => [item.session_id, item])).values())
  const totalAmount = uniqueItems.reduce((sum, item) => sum + item.amount_cents, 0)

  const { data: note, error: noteError } = await supabase
    .from('honorarium_notes')
    .insert({
      trainer_id: payload.trainerId,
      period_start: format(payload.periodStart, 'yyyy-MM-dd'),
      period_end: format(payload.periodEnd, 'yyyy-MM-dd'),
      total_amount_cents: totalAmount,
      status: 'draft',
    })
    .select()
    .single()

  if (noteError) {
    throw normalizeError(noteError)
  }

  const { error: itemsError } = await supabase.from('honorarium_note_items').insert(
    uniqueItems.map((item) => ({
      ...item,
      note_id: note.id,
    })),
  )

  if (itemsError) {
    await supabase.from('honorarium_notes').delete().eq('id', note.id)
    throw normalizeError(itemsError)
  }

  return note as HonorariumNote
}

export async function getHonorariumNotes(trainerId: string): Promise<HonorariumNote[]> {
  const { data, error } = await supabase
    .from('honorarium_notes')
    .select('*')
    .eq('trainer_id', trainerId)
    .order('period_start', { ascending: false })

  if (error) {
    throw normalizeError(error)
  }

  return data as HonorariumNote[]
}

export async function getHonorariumNoteDetails(
  noteId: string,
): Promise<HonorariumNote & { items: HonorariumNoteItem[] }> {
  const { data: note, error: noteError } = await supabase
    .from('honorarium_notes')
    .select('*')
    .eq('id', noteId)
    .single()

  if (noteError) {
    throw normalizeError(noteError)
  }

  const { data: items, error: itemsError } = await supabase
    .from('honorarium_note_items')
    .select(`
      *,
      session:course_sessions(
        id,
        starts_at,
        ends_at,
        courses(name, level)
      )
    `)
    .eq('note_id', noteId)

  if (itemsError) {
    throw normalizeError(itemsError)
  }

  return {
    ...note,
    status: note.status as HonorariumNoteStatus,
    items: items as HonorariumNoteItem[],
  }
}

export async function updateHonorariumNoteStatus(
  noteId: string,
  status: HonorariumNoteStatus,
): Promise<void> {
  const { error } = await supabase.from('honorarium_notes').update({ status }).eq('id', noteId)

  if (error) {
    throw normalizeError(error)
  }
}

export async function deleteHonorariumNote(noteId: string): Promise<void> {
  const { error } = await supabase.from('honorarium_notes').delete().eq('id', noteId)
  if (error) {
    throw normalizeError(error)
  }
}
export async function getAdminPurchasedPackagesReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<PurchasedPackageReportItem>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await (supabase.rpc as any)(
    'get_admin_report_purchased_package_payments',
    {
      p_from: params.from,
      p_to: params.to,
    },
    { count: 'exact' },
  )
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  const items = (data ?? []).map((row: any) => ({
    id: String(row.id),
    user_id: String(row.user_id),
    full_name: row.full_name ?? null,
    email: String(row.email ?? ''),
    package_type: row.package_type,
    package_name: String(row.package_name ?? row.package_type),
    amount_paid_cents: Number(row.amount_paid_cents ?? 0),
    credits_included: Number(row.credits_included ?? 0),
    created_at: String(row.created_at),
  }))

  return {
    data: items,
    total: count ?? 0,
  }
}

export async function getAdminTokenUsageReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<TokenUsageReportItem>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await (supabase.rpc as any)('get_admin_report_token_usage', {
    p_from: params.from,
    p_to: params.to,
  }, { count: 'exact' })
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  return {
    data: (data as unknown) as TokenUsageReportItem[],
    total: count ?? 0,
  }
}

export async function getAdminVoucherRedemptionsReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<VoucherRedemptionReportItem>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await callRpc<Record<string, unknown>[]>(
    'get_admin_report_voucher_redemptions',
    {
      p_from: params.from,
      p_to: params.to,
    },
    { count: 'exact' },
  )
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  return {
    data: ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      redemption_id: String(row.redemption_id),
      redeemed_at: String(row.redeemed_at),
      voucher_code: String(row.voucher_code),
      user_id: String(row.user_id),
      full_name: (row.full_name as string | null) ?? null,
      email: String(row.email ?? ''),
      credits_granted: Number(row.credits_granted ?? 0),
    })),
    total: count ?? 0,
  }
}

export async function getAdminAttendanceReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<AttendanceReportItem>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await supabase
    .from('course_sessions')
    .select(
      `
      id,
      starts_at,
      max_participants,
      course:courses(name),
      trainer:profiles!course_sessions_trainer_id_fkey(full_name),
      resource:resources(name),
      bookings(id, status)
    `,
      { count: 'exact' },
    )
    .gte('starts_at', params.from)
    .lte('starts_at', params.to)
    .eq('is_cancelled', false)
    .order('starts_at', { ascending: true })
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  const items = (data ?? []).map((row: any) => {
    const confirmedCount = (row.bookings ?? []).filter((b: any) => b.status === 'confirmed').length
    return {
      session_id: row.id,
      course_name: row.course?.name ?? 'Unbekannt',
      trainer_name: row.trainer?.full_name ?? null,
      resource_name: row.resource?.name ?? null,
      starts_at: row.starts_at,
      max_participants: row.max_participants,
      confirmed_count: confirmedCount,
      utilization_percent: row.max_participants > 0 ? (confirmedCount / row.max_participants) * 100 : 0,
    }
  })

  return {
    data: items,
    total: count ?? 0,
  }
}

export async function getAdminFinancialReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<FinancialReportItem>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await (supabase.rpc as any)('get_admin_report_financial', {
    p_from: params.from,
    p_to: params.to,
  }, { count: 'exact' })
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  return {
    data: (data as unknown) as FinancialReportItem[],
    total: count ?? 0,
  }
}

export async function getAdminTrainerActivityReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<TrainerActivityReportItem>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await (supabase.rpc as any)('get_admin_report_trainer_activity', {
    p_from: params.from,
    p_to: params.to,
  }, { count: 'exact' })
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  return {
    data: (data as unknown) as TrainerActivityReportItem[],
    total: count ?? 0,
  }
}

export async function getAdminUserGrowthReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<UserGrowthReport>> {
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error, count } = await (supabase.rpc as any)('get_admin_report_user_growth', {
    p_from: params.from,
    p_to: params.to,
  }, { count: 'exact' })
    .range(from, to)

  if (error) {
    throw normalizeError(error)
  }

  return {
    data: (data as unknown) as UserGrowthReport[],
    total: count ?? 0,
  }
}

export async function getResourceCategories(): Promise<ResourceCategory[]> {
  const { data, error } = await supabase
    .from('resource_categories')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as ResourceCategory[]
}

export async function createResourceCategory(payload: {
  name: string
  visual_type: string
  config: any
}): Promise<ResourceCategory> {
  const { data, error } = await supabase
    .from('resource_categories')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw normalizeError(error)
  }

  return data as ResourceCategory
}

export async function updateResourceCategory(
  id: string,
  payload: {
    name: string
    visual_type: string
    config: any
  },
): Promise<void> {
  const { error } = await supabase.from('resource_categories').update(payload).eq('id', id)
  if (error) {
    throw normalizeError(error)
  }
}

export async function deleteResourceCategory(id: string): Promise<void> {
  const { error } = await supabase.from('resource_categories').delete().eq('id', id)
  if (error) {
    throw normalizeError(error)
  }
}

export async function getResources(categoryId?: string): Promise<Resource[]> {
  let query = supabase
    .from('resources')
    .select('*, category:resource_categories(*)')
    .order('order_index', { ascending: true })

  if (categoryId) {
    query = query.eq('category_id', categoryId)
  }

  const { data, error } = await query

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as Resource[]
}

export async function upsertResource(resource: Partial<Resource>): Promise<void> {
  const { category_id, name, category, ...rest } = resource as any
  const payload: any = { ...rest }
  if (category_id) payload.category_id = category_id
  if (name) payload.name = name

  const { error } = await supabase.from('resources').upsert(payload)
  if (error) {
    throw normalizeError(error)
  }
}

export async function deleteResource(id: string): Promise<void> {
  const { error } = await supabase.from('resources').delete().eq('id', id)
  if (error) {
    throw normalizeError(error)
  }
}

export async function getNotificationTemplates(): Promise<NotificationTemplate[]> {
  const { data, error } = await (supabase as any)
    .from('notification_templates')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as NotificationTemplate[]
}

export async function createNotificationTemplate(
  payload: Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>,
): Promise<NotificationTemplate> {
  const { data, error } = await (supabase as any)
    .from('notification_templates')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw normalizeError(error)
  }

  return data as NotificationTemplate
}

export async function updateNotificationTemplate(
  templateId: string,
  payload: Partial<Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>>,
): Promise<NotificationTemplate> {
  const { data, error } = await (supabase as any)
    .from('notification_templates')
    .update(payload)
    .eq('id', templateId)
    .select()
    .single()

  if (error) {
    throw normalizeError(error)
  }

  return data as NotificationTemplate
}

export async function deleteNotificationTemplate(templateId: string): Promise<void> {
  const { error } = await (supabase as any).from('notification_templates').delete().eq('id', templateId)

  if (error) {
    throw normalizeError(error)
  }
}

export async function getManualNotificationRecipients(payload: {
  userIds?: string[]
  roles?: string[]
  sessionId?: string
}): Promise<{ recipient_name: string | null; recipient_email: string }[]> {
  const { data, error } = await (supabase.rpc as Function)('preview_manual_notification_recipients', {
    p_user_ids: payload.userIds && payload.userIds.length > 0 ? payload.userIds : null,
    p_roles: payload.roles && payload.roles.length > 0 ? payload.roles : null,
    p_session_id: payload.sessionId || null,
  })

  if (error) {
    throw normalizeError(error)
  }

  return (data ?? []) as { recipient_name: string | null; recipient_email: string }[]
}

export async function enqueueManualNotification(payload: {
  templateSlug: string
  userIds?: string[]
  roles?: string[]
  sessionId?: string
  customSubject?: string
  customBody?: string
  isBulk?: boolean
}): Promise<number> {
  const { data, error } = await supabase.rpc('enqueue_manual_notification', {
    p_template_slug: payload.templateSlug,
    p_user_ids: payload.userIds && payload.userIds.length > 0 ? payload.userIds : undefined,
    p_roles: payload.roles && payload.roles.length > 0 ? payload.roles : undefined,
    p_session_id: payload.sessionId || undefined,
    p_custom_subject: payload.customSubject || undefined,
    p_custom_body: payload.customBody || undefined,
    p_is_bulk: payload.isBulk ?? false,
  })

  if (error) {
    throw normalizeError(error)
  }

  return Number(data ?? 0)
}

export async function testNotificationTemplate(payload: {
  email: string
  templateSlug: string
  subject?: string
  body?: string
}): Promise<void> {
  const { error } = await supabase.functions.invoke('session-notifications', {
    body: {
      action: 'test',
      email: payload.email,
      template_slug: payload.templateSlug,
      subject: payload.subject,
      body: payload.body,
    },
  })

  if (error) {
    const functionErrorCode = await extractFunctionErrorCode(error)
    if (functionErrorCode) {
      throw new Error(functionErrorCode)
    }
    throw normalizeError(error)
  }
}
